import { FirebaseError } from "firebase/app";
import { Link, useNavigate, useSearchParams } from "@solidjs/router";
import {
  createMemo,
  createSignal,
  Match,
  Show,
  Switch,
  on,
  VoidComponent,
} from "solid-js";
import { z } from "zod";
import { useAuth } from "~/components/AuthProvider";
import PageSection from "~/components/PageSection";
import FormGroup from "~/components/FormGroup";
import StringFormControl from "~/components/StringFormControl";
import Button from "~/components/Button";
import CheckboxFormControl from "~/components/CheckboxFormControl";
import { createZodForm } from "~/zod-forms";
import { useDebounce } from "~/components/debounce";
import { OneSysHeader } from "~/components/OneSysHeader";
import { OneSysFooter } from "~/components/OneSysFooter";
import SearchInvoice from "~/components/SearchInvoice";
import { InvoiceDataOutput } from "~/trpc/api/invoices";

const signInFormData = z.object({
  email: z.string().email({ message: "Μη έγκυρη διεύθυνση email" }),
  password: z.string().min(1, { message: "Απαιτείται κωδικός πρόσβασης" }),
  storeCredentials: z.boolean().optional().default(false),
});

const SignInPage: VoidComponent = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitError, setSubmitError] = createSignal<unknown>();
  const authErrorCode = createMemo(
    on(submitError, (error) =>
      error instanceof FirebaseError ? error.code : undefined
    )
  );

  const [Form, form] = createZodForm({
    schema: signInFormData,
    initialInput: { storeCredentials: false },
  });
  const [email, setEmail] = form.bind("email");
  const [password, setPassword] = form.bind("password");
  const [storeCredentials, setStoreCredentials] = form.bind("storeCredentials");

  let emailRef: HTMLInputElement | undefined;
  const retry = () => {
    setSubmitError();
    form.reset({ storeCredentials: false });
    emailRef?.focus();
  };
  const RetryLink = () => (
    <Link
      class="navigation-link text-light"
      href="/users/sign-in"
      onClick={retry}
      style="text-decoration: underline;"
    >
      Δοκιμάστε ξανά
    </Link>
  );
  const OnError = () => (
    <Switch
      fallback={
        <p class="text-light" style="height:30vh;">
          Παρουσιάστηκε σφάλμα, <RetryLink />.
        </p>
      }
    >
      <Match when={authErrorCode() == "auth/wrong-password"}>
        {/* <div class="card mb-3 text-light"> */}
        <div style="height:30vh">
          <div class="text-light">Λάθος στοιχεία εισόδου.</div>
          <div class="text-light">
            <p>
              Αν ξεχάσατε τον κωδικό πρόσβασης μπορείτε να ζητήσετε{" "}
              <Link
                class="navigation-link text-light"
                href="/users/reset-password"
                style="text-decoration:underline;"
              >
                αλλαγή κωδικού πρόσβασης εδώ
              </Link>
              .
            </p>
            <p>
              Αλλιώς <RetryLink />.
            </p>
          </div>
        </div>
        {/* </div> */}
      </Match>
      <Match when={authErrorCode() === "auth/too-many-requests"}>
        <p class="text-light" style="height:32vh;">
          Ο λογαριασμός σας έχει κλειδωθεί προσωρινά έπειτα από πολλαπλές
          ανεπιτυχείς προσπάθειες εισόδου. <RetryLink />
        </p>
      </Match>
    </Switch>
  );

  return (
    <>
      <OneSysHeader></OneSysHeader>
      <div class="container-fluid d-flex justify-content-center align-items-center bg-image">
        <div
          class="mt-5 p-4 ms-5 me-5 container-fluid d-flex flex-column align-items-center no-margin-left no-margin-right"
          style="z-index:1;"
        >
          <PageSection>
            <div class="text-align-center">
              <h2 class="ms-3 mb-3 text-light">Είσοδος</h2>
              <Show when={submitError() === undefined} fallback={<OnError />}>
                <Form
                  class="col-6 text-light w300px mobile-w100per"
                  handleSubmit={async (values) => {
                    try {
                      await auth.signIn(values);
                      navigate(searchParams.redirect || "/dashboard");
                    } catch (error) {
                      setSubmitError(error);
                    }
                  }}
                >
                  <FormGroup>
                    <StringFormControl
                      ref={emailRef}
                      value={email() ?? ""}
                      onInput={(e) =>
                        useDebounce(setEmail)(e.currentTarget.value)
                      }
                      errors={email.dirty && email.issues}
                      type="email"
                      label="Διεύθυνση email"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <StringFormControl
                      value={password() ?? ""}
                      onInput={(e) =>
                        useDebounce(setPassword)(e.currentTarget.value)
                      }
                      errors={password.dirty && password.issues}
                      type="password"
                      label="Κωδικός πρόσβασης"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <CheckboxFormControl
                      checked={storeCredentials() ?? false}
                      onChange={(e) =>
                        setStoreCredentials(e.currentTarget.checked)
                      }
                      label="Αποθήκευση στοιχείων εισόδου"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Button
                      type="submit"
                      disabled={
                        form.submitting() ||
                        (form.lastSubmit()?.success === false && !form.valid())
                      }
                      kind="primary"
                    >
                      Είσοδος
                    </Button>
                  </FormGroup>
                </Form>
                <footer>
                  <nav>
                    <div class="ms-3 mb-2">
                      <Link
                        href="/users/reset-password"
                        class="text-light navigation-link"
                      >
                        Ξεχάσατε τον κωδικό πρόσβασης;
                      </Link>
                    </div>
                    <div class="ms-3 mb-2">
                      <Link
                        href="/users/sign-up"
                        class="text-light navigation-link"
                      >
                        Δημιουργία λογαριασμού
                      </Link>
                    </div>
                  </nav>
                </footer>
              </Show>
            </div>
          </PageSection>
        </div>
        <div class="light-layer"></div>
      </div>
      <SearchInvoice
        onSuccess={(result: InvoiceDataOutput) => {
          window.location.href = `/invoices/${result.id}`;
        }}
      ></SearchInvoice>
      <OneSysFooter></OneSysFooter>
    </>
  );
};
export default SignInPage;
